import * as m from '@/paraglide/messages';
import { cn } from '@listening/utils/react-helpers';
import IconDevices from '@listening/assets/astro/icons/icon-devices.svg?react';
import IconWeb from '@listening/assets/astro/icons/icon-web.svg?react';

import { Button } from '@listening/frontend/components/radix/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@listening/frontend/components/radix/dropdown-menu';
import { Skeleton } from '@listening/frontend/components/radix/skeleton';
import { Download, NavArrowDown, OpenNewWindow } from 'iconoir-react';
import type { ComponentType } from 'react';
import { useIsClient } from 'usehooks-ts';
import { useSystemInfo } from '@/server/lib/utm';
import { clientEnv } from '@/env';

const ANDROID_LINK =
  'https://play.google.com/store/apps/details?id=io.listening.main&hl=en&gl=US';
const IOS_LINK =
  'https://apps.apple.com/us/app/listening-read-on-the-go/id1635430768';
const DESKTOP_AUTODETECT_LINK = '/download';
const DESKTOP_WINDOWS_X64_LINK = '/download/windows?arch=x64';
const DESKTOP_WINDOWS_ARM64_LINK = '/download/windows?arch=arm64';
const DESKTOP_MACOS_UNIVERSAL_LINK = '/download/macos?arch=universal';
const DESKTOP_MACOS_ARM64_LINK = '/download/macos?arch=arm64';
const DESKTOP_MACOS_X64_LINK = '/download/macos?arch=x64';
const WEBAPP_LINK = 'https://www.listening.com/app/login';

const primaryCtaClassName =
  'relative h-auto w-full rounded-full border-b-4 border-cloud-375 bg-royal-50 px-8 py-4 text-lg font-medium text-white hover:bg-royal-50/90 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden';

function detectMobilePlatform(systemInfo: string): 'android' | 'ios' | 'other' {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/android/i.test(systemInfo) || /android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(systemInfo) || /iphone|ipad|ipod/i.test(ua))
    return 'ios';
  return 'other';
}

type DesktopCta = {
  href: string;
  label: string;
  compatibilityLabel: string;
};

function resolveDesktopCta(systemInfo: string): DesktopCta | null {
  if (systemInfo === 'Mac Apple Chip' || systemInfo === 'Mac Intel Chip') {
    return {
      href: DESKTOP_MACOS_UNIVERSAL_LINK,
      label: m.downloads_macos_cta(),
      compatibilityLabel: m.downloads_compatibility_macos(),
    };
  }
  if (systemInfo === 'Windows 64-bit') {
    return {
      href: DESKTOP_WINDOWS_X64_LINK,
      label: m.downloads_windows_cta(),
      compatibilityLabel: m.downloads_compatibility_windows(),
    };
  }
  return null;
}

function DownloadButtonSkeleton() {
  return (
    <Button
      aria-busy='true'
      aria-label={m.downloads_desktop_cta()}
      className={primaryCtaClassName}
      disabled
      size='lg'
      variant='gradient'
    >
      <div className='flex items-center justify-center gap-3'>
        <IconDevices aria-hidden='true' className='size-5' />
        <Skeleton
          aria-hidden='true'
          className='h-6 w-40 rounded-full bg-white/35'
        />
      </div>
    </Button>
  );
}

export function DownloadButtonSwitcher() {
  const isClient = useIsClient();
  const systemInfo = useSystemInfo();

  if (!isClient) {
    return <DownloadButtonSkeleton />;
  }

  const mobilePlatform = detectMobilePlatform(systemInfo);

  if (mobilePlatform === 'android') {
    return (
      <Button
        asChild
        className={primaryCtaClassName}
        size='lg'
        variant='gradient'
      >
        <a
          aria-label={m.downloads_google_play()}
          className='flex items-center justify-center gap-3'
          href={ANDROID_LINK}
          rel='noopener noreferrer'
          target='_blank'
        >
          <IconDevices aria-hidden='true' className='size-5' />
          <span>{m.downloads_google_play()}</span>
          <OpenNewWindow aria-hidden='true' className='ml-1 size-4' />
        </a>
      </Button>
    );
  }
  if (mobilePlatform === 'ios') {
    return (
      <Button
        asChild
        className={primaryCtaClassName}
        size='lg'
        variant='gradient'
      >
        <a
          aria-label={m.downloads_app_store()}
          className='flex items-center justify-center gap-3'
          href={IOS_LINK}
          rel='noopener noreferrer'
          target='_blank'
        >
          <IconDevices aria-hidden='true' className='size-5' />
          <span>{m.downloads_app_store()}</span>
          <OpenNewWindow aria-hidden='true' className='ml-1 size-4' />
        </a>
      </Button>
    );
  }

  if (clientEnv.desktopDownloadsEnabled) {
    const desktopCta = resolveDesktopCta(systemInfo) ?? {
      href: DESKTOP_AUTODETECT_LINK,
      label: m.downloads_desktop_cta(),
      compatibilityLabel: m.downloads_compatibility_desktop(),
    };

    return (
      <div className='flex w-full flex-col items-center gap-3'>
        <Button
          asChild
          className={primaryCtaClassName}
          size='lg'
          variant='gradient'
        >
          <a
            aria-label={desktopCta.label}
            className='flex items-center justify-center gap-3'
            href={desktopCta.href}
          >
            <IconDevices aria-hidden='true' className='size-5' />
            <span>{desktopCta.label}</span>
          </a>
        </Button>
        <p className='max-w-xs text-center text-sm leading-5 font-medium text-neutral-665'>
          {desktopCta.compatibilityLabel}
        </p>
      </div>
    );
  }

  return (
    <Button
      asChild
      className={primaryCtaClassName}
      size='lg'
      variant='gradient'
    >
      <a
        aria-label={m.downloads_web_app()}
        className='flex items-center justify-center gap-3'
        href={WEBAPP_LINK}
        rel='noopener noreferrer'
        target='_blank'
      >
        <IconWeb aria-hidden='true' className='size-5' />
        <span>{m.downloads_web_app()}</span>
        <OpenNewWindow aria-hidden='true' className='ml-1 size-4' />
      </a>
    </Button>
  );
}

type DownloadLink = {
  name: string;
  href: string;
  download?: boolean;
  openInNewTab?: boolean;
};

type DownloadOptionProps = {
  title: string;
  icon: ComponentType<{ className?: string }>;
  links: ReadonlyArray<DownloadLink>;
};

const optionTriggerClassName = cn(
  'flex h-auto w-full max-w-[312px] flex-col items-center justify-center gap-3 rounded-[32px] border border-neutral-200 bg-white p-4 transition-all hover:border-neutral-300 hover:bg-neutral-50',
  'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden',
);

function DownloadOption({ title, icon: Icon, links }: DownloadOptionProps) {
  if (links.length === 0) return null;

  if (links.length === 1) {
    const [link] = links;
    if (!link) return null;

    const trailingIcon = link.openInNewTab ? (
      <OpenNewWindow aria-hidden='true' className='size-5 text-neutral-600' />
    ) : link.download ? (
      <Download aria-hidden='true' className='size-5 text-neutral-600' />
    ) : null;

    return (
      <Button asChild className={optionTriggerClassName} variant='outline'>
        <a
          aria-label={`${link.download ? m.common_download() : m.common_open()} ${link.name}`}
          download={link.download}
          href={link.href}
          rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
          target={link.openInNewTab ? '_blank' : undefined}
        >
          <div className='flex items-center justify-center gap-3'>
            <Icon aria-hidden='true' className='size-5 text-neutral-600' />
            <span className='text-lg leading-7 font-medium text-neutral-600'>
              {title}
            </span>
            {trailingIcon}
          </div>
        </a>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className={optionTriggerClassName} variant='outline'>
          <div className='flex items-center justify-center gap-3'>
            <Icon aria-hidden='true' className='size-5 text-neutral-600' />
            <span className='text-lg leading-7 font-medium text-neutral-600'>
              {title}
            </span>
            <NavArrowDown
              aria-hidden='true'
              className='size-5 text-neutral-600'
            />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='center'
        className='w-[312px] rounded-2xl border border-neutral-200 bg-white p-2 shadow-lg'
      >
        {links.map((link) => (
          <DropdownMenuItem asChild className='p-0' key={link.href}>
            <a
              aria-label={`${link.download ? m.common_download() : m.common_open()} ${link.name}`}
              className={cn(
                'flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-neutral-700 transition-colors',
                'hover:bg-azure-595/5 hover:text-neutral-900 focus:bg-azure-595/5 focus:text-neutral-900 focus:outline-hidden',
              )}
              download={link.download}
              href={link.href}
              rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
              target={link.openInNewTab ? '_blank' : undefined}
            >
              {link.download ? (
                <Download aria-hidden='true' className='size-4' />
              ) : (
                <OpenNewWindow aria-hidden='true' className='size-4' />
              )}
              <span className='text-center text-base leading-snug font-medium'>
                {link.name}
              </span>
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DownloadDropdownSwitcher() {
  return (
    <>
      {clientEnv.desktopDownloadsEnabled ? (
        <DownloadOption
          icon={IconDevices}
          links={[
            {
              name: m.downloads_dropdown_macos_universal(),
              href: DESKTOP_MACOS_UNIVERSAL_LINK,
              download: true,
            },
            {
              name: m.downloads_dropdown_macos_apple_silicon(),
              href: DESKTOP_MACOS_ARM64_LINK,
              download: true,
            },
            {
              name: m.downloads_dropdown_macos_intel(),
              href: DESKTOP_MACOS_X64_LINK,
              download: true,
            },
            {
              name: m.downloads_dropdown_windows_x64(),
              href: DESKTOP_WINDOWS_X64_LINK,
              download: true,
            },
            {
              name: m.downloads_dropdown_windows_arm64(),
              href: DESKTOP_WINDOWS_ARM64_LINK,
              download: true,
            },
          ]}
          title={m.downloads_dropdown_desktop()}
        />
      ) : null}

      <DownloadOption
        icon={IconWeb}
        links={[
          {
            name: m.downloads_dropdown_web_app(),
            href: WEBAPP_LINK,
            openInNewTab: true,
          },
        ]}
        title={m.downloads_dropdown_web_app()}
      />

      <DownloadOption
        icon={IconWeb}
        links={[
          {
            name: m.common_chrome_extension(),
            href: clientEnv.chromeStoreUrl,
            openInNewTab: true,
          },
        ]}
        title={m.downloads_dropdown_extension()}
      />

      <DownloadOption
        icon={IconDevices}
        links={[
          { name: 'Android', href: ANDROID_LINK, openInNewTab: true },
          { name: 'iOS', href: IOS_LINK, openInNewTab: true },
        ]}
        title={m.downloads_dropdown_mobile()}
      />
    </>
  );
}
